import { Loading } from "components/Loading";
import { PlaceHolderNotExists } from "components/PlaceholderNotExists";
import { TableModifiedPagination } from "components/TableModified/TableModifiedPagination";
import { TableModifiedTable } from "components/TableModified/TableModifiedTable";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import React, { useEffect, useState } from "react";
import { BaseRoutes } from "routes/BaseRoutes";
import {
  BATCH_STATUS_LABELS,
  EMPTY,
  formatDateTime,
  orEmpty,
} from "../formatters";

interface TicketIngestionBatch {
  ticketIngestionBatchId: string;
  batchId?: string | null;
  eventSlug: string;
  source: string;
  status: string;
  error?: string | null;
  received: number;
  created: number;
  updated: number;
  unchanged: number;
  rejected: number;
  errors: { externalTicketId?: string | null; reason: string }[];
  warnings: { externalTicketId?: string | null; reason: string }[];
  insertDate: string;
}

const tableHead = [
  "Recebido em",
  "Origem",
  "Evento",
  "Lote",
  "Status",
  "Recebidos",
  "Criados",
  "Atualizados",
  "Sem mudança",
  "Rejeitados",
  "Erros",
  "Avisos",
];

const QUERY = `
  query BackofficeTicketIngestionBatchesPage($pagination: PaginationInput) {
    backofficeTicketIngestionBatchesPage(pagination: $pagination) {
      items {
        ticketIngestionBatchId
        batchId
        eventSlug
        source
        status
        error
        received
        created
        updated
        unchanged
        rejected
        errors {
          externalTicketId
          reason
        }
        warnings {
          externalTicketId
          reason
        }
        insertDate
      }
      pagination {
        totalCount
      }
    }
  }
`;

const formatIssueLines = (lines: string[]) => {
  if (lines.length === 0) return EMPTY;
  return (
    <div>
      {lines.map((line, index) => (
        <div key={index}>{line}</div>
      ))}
    </div>
  );
};

const formatErrors = (batch: TicketIngestionBatch) =>
  formatIssueLines([
    ...(batch.error ? [batch.error] : []),
    ...batch.errors.map(
      (item) => `${item.externalTicketId ?? "sem id"}: ${item.reason}`
    ),
  ]);

// Avisos: item aceito com campo descartado (ex.: telefone fora de E.164).
// Lotes anteriores à mudança chegam com a lista vazia.
const formatWarnings = (batch: TicketIngestionBatch) =>
  formatIssueLines(
    (batch.warnings ?? []).map(
      (item) => `${item.externalTicketId ?? "sem id"}: ${item.reason}`
    )
  );

// O payload abre em outra aba para não perder a página e o filtro atuais.
const openPayload = (batch: TicketIngestionBatch) => {
  window.open(
    BaseRoutes.ticketIngestionBatchPayload.replace(
      ":id",
      batch.ticketIngestionBatchId
    ),
    "_blank",
    "noopener"
  );
};

export const TicketIngestionBatches: React.FC = () => {
  const { connectGraphQLApi } = useGraphQLAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [batches, setBatches] = useState<TicketIngestionBatch[]>([]);
  const [rows, setRows] = useState<Object[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setLoading(true);
    connectGraphQLApi({
      query: QUERY,
      variables: {
        pagination: {
          offset: (pageNumber - 1) * rowsPerPage,
          limit: rowsPerPage,
        },
      },
      errorMessage: "Falha ao listar os lotes recebidos",
      useNewApi: true,
    })
      .then((response: any) => {
        const { items, pagination } =
          response.data.backofficeTicketIngestionBatchesPage;
        const batches: TicketIngestionBatch[] = items || [];
        setBatches(batches);
        setTotalCount(pagination.totalCount);
        setRows(
          batches.map((batch) => ({
            insertDate: formatDateTime(batch.insertDate),
            source: batch.source,
            eventSlug: batch.eventSlug,
            batchId: orEmpty(batch.batchId),
            status: BATCH_STATUS_LABELS[batch.status] ?? batch.status,
            received: batch.received,
            created: batch.created,
            updated: batch.updated,
            unchanged: batch.unchanged,
            rejected: batch.rejected,
            errors: formatErrors(batch),
            warnings: formatWarnings(batch),
          }))
        );
        setError(false);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, rowsPerPage]);

  const handleRowClick = (_row: any, rowIndex: number): void => {
    const batch = batches[rowIndex];
    if (batch) openPayload(batch);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    setRowsPerPage(parseInt(event.target.value));
    setPageNumber(1);
  };

  return (
    <div>
      <h2 style={{ marginBottom: "1rem" }}>Lotes recebidos</h2>
      <p style={{ marginBottom: "1rem" }}>
        Clique em um lote para ver o payload enviado pelo parceiro.
      </p>
      {loading ? (
        <Loading />
      ) : error ? (
        <PlaceHolderNotExists title="Erro ao se comunicar com o servidor" />
      ) : rows.length === 0 ? (
        <PlaceHolderNotExists title="Nenhum lote recebido até o momento" />
      ) : (
        <TableModifiedTable
          headerColumns={tableHead}
          rows={rows}
          onRowClick={handleRowClick}
        />
      )}

      {totalCount > 0 ? (
        <TableModifiedPagination
          totalItemsCount={totalCount}
          pageNumber={pageNumber}
          rowsPerPage={rowsPerPage}
          onPageSelected={setPageNumber}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      ) : null}
    </div>
  );
};
